import * as d3 from 'd3';
import round from 'lodash-es/round';

import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import TurbomapNodeFactory from './turbomap-node-factory';
import {
	createProductMap,
	formatNumberInteger,
	getProductDirectionClass,
	getScaleMax,
	getScaleMin,
	backgroundBarZebraClass,
} from './turbomap-utils';

import { default as settings, margins } from './turbomap-settings';

// Colors hardcoded, available in colors.scss too
const COLOR_PUT = '#a5ffa5';
const COLOR_CALL = '#9aeefa';

class TurbomapVisual {
	constructor(el) {
		this.el = el;

		const axisContainer = this.el.querySelector('.js-axis-y');
		const areaContainer = this.el.querySelector('.js-area');

		if (!axisContainer || !areaContainer) {
			throw Error('Turbomap needs an axis and area element');
		}

		this.create();
		this.update();
	}

	get isEmpty() {
		return this.el.classList.contains('is-empty');
	}

	set isEmpty(bool) {
		this.el.classList.toggle('is-empty', bool);
	}

	create() {
		this.createContainers();
		this.defineCombinedGradientSvg();
		this.createAxis();
	}

	createContainers() {
		const { height, axisY: { width: axisWidth } } = settings;

		this.axisYSvg = d3.select('.js-axis-y')
			.append('svg')
			.attr('width', axisWidth)
			.attr('height', height)
			.attr('class', 'turbomap__svg');

		this.axisYContainer = this.axisYSvg
			.append('g')
			.attr('transform', `translate(${axisWidth - 2}, ${margins.top})`);

		this.svg = d3.select('.js-area')
			.append('svg')
			.attr('height', height)
			.attr('class', 'turbomap__svg');

		this.areaContainer = this.svg
			.append('g')
			.attr('transform', `translate(0, ${margins.top})`);
	}

	createAxis() {
		this.axisXElement = this.areaContainer.append('g')
			.attr('class', 'axis-x turbomap__axis turbomap__axis--x');

		this.axisYElement = this.axisYContainer.append('g')
			.attr('class', 'axis-y turbomap__axis turbomap__axis--y');
	}

	update(data, isZoom) {
		this.prepareData(isZoom, data);

		this.isEmpty = this.products.length === 0;

		this.updateSize();
		this.updateAxisX();
		this.updateAxisY();

		this.drawBackgroundBars();
		this.drawGridlines();
		this.updateProducts();
		this.drawBaseline(isZoom);
	}

	prepareData(isZoom, data = {}) {
		const { barrierBaseLine = 50, dates = ['-'], products = [] } = data;
		const { axisY: { numTicks } } = settings;

		// fail fast
		if (!barrierBaseLine || !dates) {
			this.products = [];

			return;
		}

		// round baseline and ticks to same decimals
		// otherwise tick for example is 1.234, baseline is 1.23
		// and d3 can't plot the baseline on the axis
		this.barrierBaseLine = round(barrierBaseLine, 2);
		this.dates = dates;
		this.products = products;

		const scaleMin = getScaleMin(isZoom, data);
		const scaleMax = getScaleMax(isZoom, data);
		const interpolate = d3.interpolateNumber(scaleMin, scaleMax);

		this.ticks = new Array(numTicks)
			.fill()
			.map((_, i) => {
				const interpolatedValue = interpolate(i / (numTicks - 1));

				return round(interpolatedValue, 2);
			});

		// Pop the last tick so no empty rows
		this.ticks.pop();

		this.productMap = createProductMap(this.products, this.dates, this.ticks);
	}

	updateProducts() {
		if (this.isEmpty) {
			d3.selectAll('g.day-barrier-group').remove();
		} else {
			const columnsSelection = this.layoutColumns();
			const rowsSelection = this.layoutDayBarrierGroups(columnsSelection, this.ticks);

			this.fillBarrierBuckets(rowsSelection);
		}
	}

	updateSize() {
		const areaElement = this.el.querySelector('.js-area');
		const areaElementWidth = areaElement.clientWidth;
		const { bands: { minimalWidth: bandMinimalWidth } } = settings;

		const { length } = this.dates;
		const numBands = Math.max(1, length);

		const bandAvailableWidth = areaElementWidth / numBands;
		const bandwidth = Math.max(bandMinimalWidth, bandAvailableWidth);

		settings.width = bandwidth * numBands;

		this.svg.attr('width', settings.width);
	}

	updateAxisX() {
		this.scaleX = d3.scaleBand()
			.domain(this.dates)
			.range([0, settings.width]);

		const axis = d3.axisTop(this.scaleX);

		this.axisXElement
			.transition()
			.call(axis);
	}

	updateAxisY() {
		this.scaleY = d3.scaleBand()
			.domain(this.ticks)
			.range([0, settings.height - margins.top - settings.axis.lineWidth]);

		const axis = d3.axisLeft(this.scaleY)
			.tickFormat(tick => (tick === this.barrierBaseLine
				? ''
				: formatNumberInteger(tick)));

		this.axisYElement.call(axis);

		// the ticks are aligned in the middle, since it's a band scale
		// but for clarity we want them aligned top
		this.axisYElement.selectAll('.tick')
			.each((tickValue, index, selection) => {
				// in a function declaration, `this` would be the element
				d3.select(selection[index])
					.attr('transform', `translate(0, ${this.scaleY(tickValue)})`);
			});


		this.axisYElement.selectAll('text')
			.transition()
			.attr('transform', 'translate(0, 0)')
			.attr('opacity', 1);
	}

	layoutColumns() {
		return this.areaContainer.selectAll('g.date-column')
			.data(this.dates)
			.join('g')
			.attr('class', 'date-column')
			.attr('transform', date => `translate(${this.scaleX(date)}, 0)`);
	}

	layoutDayBarrierGroups(dateColumnsSelection, barriers) {
		return dateColumnsSelection.selectAll('g.day-barrier-group')
			.data(date => barriers.map(barrier => ({ barrier, date })))
			.join('g')
			.attr('class', 'day-barrier-group')
			.attr('transform', data => `translate(0, ${this.scaleY(data.barrier) + 1})`);
	}

	fillBarrierBuckets(barrierBuckets) {
		const areaWidth = this.scaleX.bandwidth();
		const areaHeight = this.scaleY.bandwidth();
		const elementsCount = this.getMaxElementsInBucket();
		const factory = new TurbomapNodeFactory(elementsCount, areaWidth, areaHeight);

		const productHolderSelection = barrierBuckets.selectAll('g.product-container')
			.data(({ date, barrier }) => {
				const mapKey = `${date}-${barrier}`;
				const products = this.productMap.get(mapKey) || [];

				return factory.createNode(products, mapKey);
			})
			.join(
				enter => enter.append('g'),
				update => update.attr('transfrom', data => data.translation),
			)
			.attr('class', 'product-container turbomap__product-container')
			.attr('id', data => data.id)
			.attr('transform', data => data.translation);

		productHolderSelection
			.on('mouseover', (event) => {
				// https://observablehq.com/@d3/d3v6-migration-guide#events
				const nodes = productHolderSelection.nodes();
				const index = nodes.indexOf(event.currentTarget);
				const el = d3.select(nodes[index]);

				el.raise();
				el.classed('is-hover', true);
			})
			.on('mouseout', (event) => {
				// https://observablehq.com/@d3/d3v6-migration-guide#events
				const nodes = productHolderSelection.nodes();
				const index = nodes.indexOf(event.currentTarget);
				const el = d3.select(nodes[index]);

				el.classed('is-hover', false);
			})
			.on('click', (event, data) => {
				this.onContainerClicked(data.clickEvent, data);
			});

		productHolderSelection
			.attr('opacity', 0)
			.transition()
			.duration(500)
			.delay((d, i) => i * 10)
			.attr('opacity', 1);

		productHolderSelection.selectAll('circle')
			.data(data => [data])
			.join(
				enter => enter.append('circle'),
				update => update.transition().attr('r', d => d.elementSize * 0.5),
			)
			.attr('class', data => `product-circle turbomap__product-circle ${getProductDirectionClass(data.direction)}`)
			.transition()
			.attr('r', d => d.elementSize * 0.5);

		productHolderSelection.selectAll('text')
			.data(data => [data])
			.join('text')
			.attr('class', 'turbomap__product-price')
			.attr('text-anchor', 'middle')
			.attr('dominant-baseline', 'middle')
			.text(p => p.label);
	}

	drawBaseline(isZoom) {
		const barrier = this.barrierBaseLine;
		const { width, height } = settings;
		let positionY = this.scaleY(barrier);

		if (isZoom) {
			const [, [firstProduct]] = this.productMap.entries().next().value;

			// minus one pixel to not let the barrier be right on the bottom axis
			positionY = firstProduct.belowBaseline ? height - margins.top - settings.axis.lineWidth - 1 : 0;
		}

		d3.select('.js-axis-y').selectAll('div.baseline-label')
			.data([barrier])
			.join('div')
			.attr('class', 'baseline-label turbomap__baseline-label')
			.style('transform', `translate(0px, ${positionY}px)`)
			.style('top', `${margins.top / 2}px`)
			.text(value => formatNumberInteger(value));

		this.areaContainer.selectAll('line.baseline')
			.data([barrier])
			.join('line')
			.attr('class', 'baseline turbomap__baseline')
			.attr('transform', `translate(${-margins.left}, ${positionY})`)
			.attr('x1', 0)
			.attr('y1', 0)
			.attr('y2', 0)
			.attr('x2', width + margins.left)
			.raise();
	}

	defineCombinedGradientSvg() {
		const defs = this.svg.append('defs');

		const gradient = defs.append('linearGradient')
			.attr('id', 'combinedGradient')
			.attr('x1', '100%')
			.attr('x2', '100%')
			.attr('y1', '0%')
			.attr('y2', '100%');

		gradient.append('stop')
			.attr('offset', '0%')
			.attr('stop-color', COLOR_PUT);

		gradient.append('stop')
			.attr('offset', '51%')
			.attr('stop-color', COLOR_PUT);

		gradient.append('stop')
			.attr('offset', '51.1%')
			.attr('stop-color', COLOR_CALL);

		gradient.append('stop')
			.attr('offset', '100%')
			.attr('stop-color', COLOR_CALL);
	}

	drawBackgroundBars() {
		const { width } = settings;
		const bandHeight = this.scaleY.bandwidth();
		const height = bandHeight;

		// reverse ticks so they're from top to bottom
		const bandData = [...this.ticks].reverse();

		this.areaContainer.selectAll('rect.turbomap__background-bar')
			.data(bandData)
			.join('rect')
			.attr('class', (d, index) => `turbomap__background-bar ${backgroundBarZebraClass(index % 2 === 0)}`)
			.attr('opacity', 0)
			.attr('x', 0)
			.attr('y', tick => this.scaleY(tick))
			.attr('width', width)
			.attr('height', height)
			.transition(2000)
			.attr('opacity', 1);
	}

	drawGridlines() {
		const { width, height } = settings;

		// horizontal
		this.areaContainer.selectAll('line.grid-line-y')
			.data(this.ticks)
			.join('line')
			.attr('class', 'turbomap__grid-line grid-line-y')
			.attr('x1', 0)
			.attr('y1', tick => this.scaleY(tick))
			.attr('x2', width)
			.attr('y2', tick => this.scaleY(tick));

		// vertical
		this.areaContainer.selectAll('line.grid-line-x')
			.data(this.dates)
			.join('line')
			.attr('class', 'turbomap__grid-line grid-line-x')
			.attr('x1', date => this.scaleX(date))
			.attr('y1', 0)
			.attr('x2', date => this.scaleX(date))
			.attr('y2', height);

		// bottom line
		this.areaContainer.selectAll('line.grid-line-bottom')
			.data([height - margins.top - margins.bottom - 2])
			.join('line')
			.attr('class', 'turbomap__grid-line turbomap__grid-line--bottom grid-line-bottom')
			.attr('x1', 0)
			.attr('y1', h => h)
			.attr('x2', width)
			.attr('y2', h => h);
	}

	getMaxElementsInBucket() {
		const productGroups = Array.from(this.productMap.values());
		const [largestGroup = []] = productGroups.sort((g1, g2) => g2.length - g1.length);

		return largestGroup.length;
	}

	onContainerClicked(eventName, detail) {
		dispatchCustomEvent(this.el, eventName, detail);
	}
}

export default TurbomapVisual;
